const BACKGROUND = 'rgb(10, 14, 24)';
const DEFAULT_MAX_BYTES = 64 * 1024 * 1024;
const RECORD_BYTES = 192;
const DEFAULT_STRIP_PIXELS = 16384;
const DEFAULT_UPLOAD_PIXELS = 131072;

function clock() { return globalThis.performance?.now?.() ?? Date.now(); }

function paletteKey(params) {
  if (!params || typeof params !== 'object') return params;
  return JSON.stringify(Object.keys(params).sort().map(key => [key, params[key]]));
}

function copyGrid(snapshot) {
  const width = snapshot?.width ?? snapshot?.canvasWidth;
  const height = snapshot?.height ?? snapshot?.canvasHeight;
  const xRange = snapshot?.xRange;
  const yRange = snapshot?.yRange;
  if (!Number.isInteger(width) || !Number.isInteger(height) || width <= 0 || height <= 0 ||
      !Array.isArray(xRange) || !Array.isArray(yRange) ||
      ![...xRange, ...yRange].every(Number.isFinite) || xRange.length !== 2 || yRange.length !== 2 ||
      xRange[1] <= xRange[0] || yRange[1] <= yRange[0]) return null;
  return Object.freeze({ width, height,
    xRange: Object.freeze([...xRange]), yRange: Object.freeze([...yRange]),
    colorKey: snapshot.colorKey ?? paletteKey(snapshot.colorParams) });
}

function sameGrid(grid, target, canvas) {
  return grid.width === canvas.width && grid.height === canvas.height &&
    grid.xRange[0] === target.xRange[0] && grid.xRange[1] === target.xRange[1] &&
    grid.yRange[0] === target.yRange[0] && grid.yRange[1] === target.yRange[1];
}

function worldRect(record) {
  if (record.worldBounds && record.worldBoundsHeight === record.height) return record.worldBounds;
  const { grid, x, y, width, height } = record;
  const sx = (grid.xRange[1] - grid.xRange[0]) / grid.width;
  const sy = (grid.yRange[1] - grid.yRange[0]) / grid.height;
  const bounds = { x: grid.xRange[0] + x * sx, y: grid.yRange[0] + y * sy,
    width: width * sx, height: height * sy, sx, sy };
  record.worldBounds = bounds;
  record.worldBoundsHeight = height;
  return bounds;
}

function containsFiner(first, second) {
  if (first.colorKey !== second.colorKey) return false;
  const a = worldRect(first), b = worldRect(second);
  return a.sx <= b.sx && a.sy <= b.sy && a.x <= b.x && a.y <= b.y &&
    a.x + a.width >= b.x + b.width && a.y + a.height >= b.y + b.height;
}

function projection(record, target, canvas) {
  const current = sameGrid(record.grid, target, canvas);
  if (current) return { x: record.x, y: record.y, width: record.width,
    height: record.uploadedRows, sampleX: 1, sampleY: 1, current };
  const { grid } = record;
  const targetX = target.xRange[1] - target.xRange[0];
  const targetY = target.yRange[1] - target.yRange[0];
  const sampleX = (grid.xRange[1] - grid.xRange[0]) / grid.width / targetX * canvas.width;
  const sampleY = (grid.yRange[1] - grid.yRange[0]) / grid.height / targetY * canvas.height;
  return { x: (grid.xRange[0] - target.xRange[0]) / targetX * canvas.width + record.x * sampleX,
    y: (grid.yRange[0] - target.yRange[0]) / targetY * canvas.height + record.y * sampleY,
    width: record.width * sampleX, height: record.uploadedRows * sampleY,
    sampleX, sampleY, current };
}

function clipped(rect, width, height) {
  const x = Math.max(0, rect.x), y = Math.max(0, rect.y);
  const right = Math.min(width, rect.x + rect.width), bottom = Math.min(height, rect.y + rect.height);
  return right > x && bottom > y ? { x, y, width: right - x, height: bottom - y } : null;
}

function intersects(record, target) {
  const rect = worldRect(record);
  return rect.x < target.xRange[1] && rect.x + rect.width > target.xRange[0] &&
    rect.y < target.yRange[1] && rect.y + rect.height > target.yRange[0];
}

// Rectangular union area without a display-sized coverage mask.
function coveredArea(rects) {
  if (rects.length === 0) return 0;
  if (rects.length === 1) return rects[0].width * rects[0].height;
  const edges = [...new Set(rects.flatMap(rect => [rect.x, rect.x + rect.width]))].sort((a, b) => a - b);
  let area = 0;
  for (let index = 1; index < edges.length; index++) {
    const left = edges[index - 1], right = edges[index];
    const spans = rects.filter(rect => rect.x < right && rect.x + rect.width > left)
      .map(rect => [rect.y, rect.y + rect.height]).sort((a, b) => a[0] - b[0]);
    let length = 0, bottom = -Infinity;
    for (const [start, end] of spans) {
      length += Math.max(0, end - Math.max(start, bottom));
      bottom = Math.max(bottom, end);
    }
    area += (right - left) * length;
  }
  return area;
}

/**
 * Project immutable CPU sources and accepted worker tiles directly into the
 * latest camera. Uploads are row strips; no displayed mixture becomes a source.
 * Returns null for minimal Canvas2D fallbacks that cannot compose original images.
 */
export function createCpuTilePresentation(canvas, { maxBytes = DEFAULT_MAX_BYTES, maxTiles = 64,
  maxStripPixels = DEFAULT_STRIP_PIXELS, maxUploadPixels = DEFAULT_UPLOAD_PIXELS,
  createCanvas = () => document.createElement('canvas') } = {}) {
  let context;
  try { context = canvas?.getContext?.('2d'); } catch (error) { return null; }
  if (!context?.drawImage || !context.putImageData || !context.fillRect) return null;
  let probe;
  try { probe = createCanvas(); } catch (error) { return null; }
  let probeContext;
  try { probeContext = probe?.getContext?.('2d'); } catch (error) { return null; }
  if (!probeContext?.putImageData || !probeContext.createImageData) return null;
  probe.width = 0;
  probe.height = 0;
  let source = null, pendingSource = null, tiles = [], disposed = false, nextId = 1;
  let target = null, targetColorKey, latestCameraRequestedAt = null;
  let stagingBytes = 0, peakBytes = canvas.width * canvas.height * 4;
  let uploadedPixels = 0, evictedTiles = 0, rejectedTiles = 0;
  let lastInstallMs = 0, lastUploadPixels = 0, budgetExhausted = false;
  let lastRegions = [], lastPresentTime = null;
  let anchorStats = { anchorCoverage: 0, anchorCurrentCoverage: 0,
    anchorMaxSourceCameraAgeMs: null, anchorMaxSampleFootprint: null, anchorMissingSamples: 0 };
  const allRecords = () => [source, pendingSource, ...tiles].filter(Boolean);
  const pixelBytes = value => value?.buffer?.byteLength ?? value?.byteLength ?? value?.length ?? 0;
  const recordBytes = record => record ? (record.canvas
    ? record.canvas.width * record.canvas.height : record.width * record.height) * 4 +
    pixelBytes(record.pixels) + RECORD_BYTES : 0;
  const residentBytes = () => canvas.width * canvas.height * 4 + stagingBytes +
    allRecords().reduce((sum, record) => sum + recordBytes(record), 0);
  const sampleBytes = () => { peakBytes = Math.max(peakBytes, residentBytes()); };

  function release(record) {
    if (!record) return;
    if (record.canvas) { record.canvas.width = 0; record.canvas.height = 0; }
    record.canvas = null;
    record.context = null;
    record.pixels = null;
  }

  function evict(index) {
    release(tiles[index]);
    tiles.splice(index, 1);
    evictedTiles++;
  }

  function evictOutsideView() {
    if (!target) return;
    for (let index = tiles.length - 1; index >= 0; index--) {
      if ((targetColorKey !== undefined && tiles[index].colorKey !== targetColorKey) ||
          !intersects(tiles[index], target)) evict(index);
    }
  }

  function admit(bytes, tileSlot = false) {
    evictOutsideView();
    if (residentBytes() + bytes > maxBytes || (tileSlot && tiles.length >= maxTiles)) {
      // Pending work has no accepted coverage to preserve. Prefer useful samples
      // already installed over speculative queued work when admission is tight.
      for (let index = 0; index < tiles.length &&
          (residentBytes() + bytes > maxBytes || (tileSlot && tiles.length >= maxTiles));) {
        if (!tiles[index].uploadedRows) evict(index);
        else index++;
      }
    }
    return residentBytes() + bytes <= maxBytes && (!tileSlot || tiles.length < maxTiles);
  }

  function recordFor(input, grid, x, y, width, height, copyPixels = true) {
    const length = width * height * 4;
    if (!input.pixels || input.pixels.length < length || !Number.isSafeInteger(length)) return null;
    return { id: nextId++, grid, x, y, width, height,
      colorKey: input.colorKey ?? grid.colorKey,
      cameraRequestedAt: Number.isFinite(input.cameraRequestedAt) ? input.cameraRequestedAt : clock(),
      pixels: copyPixels
        ? new Uint8ClampedArray(input.pixels.subarray?.(0, length) ?? input.pixels.slice(0, length))
        : input.pixels,
      canvas: null, context: null, uploadedRows: 0 };
  }

  function setSource(input) {
    if (disposed) return false;
    const grid = copyGrid(input);
    if (!grid) return false;
    if (!input.pixels || input.pixels.length < grid.width * grid.height * 4) return false;
    const bytes = grid.width * grid.height * 4 + pixelBytes(input.pixels) + RECORD_BYTES;
    const oldPending = pendingSource;
    // Preserve useful accepted strips from a superseded pending source as an
    // original tile. The backing retains its original allocation; the admitted
    // byte count uses that full backing, rather than its smaller accepted crop.
    const parked = oldPending?.uploadedRows ? { ...oldPending, pixels: null,
      height: oldPending.uploadedRows } : null;
    const keepParked = parked && (!target || intersects(parked, target)) &&
      (targetColorKey === undefined || parked.colorKey === targetColorKey);
    if (keepParked && tiles.length >= maxTiles) {
      evictOutsideView();
      for (let index = 0; index < tiles.length && tiles.length >= maxTiles;) {
        if (!tiles[index].uploadedRows) evict(index);
        else index++;
      }
      if (tiles.length >= maxTiles) return false;
    }
    pendingSource = null;
    if (keepParked) tiles.push(parked);
    if (!admit(bytes + grid.width * 4)) {
      if (keepParked) tiles.splice(tiles.indexOf(parked), 1);
      pendingSource = oldPending;
      return false;
    }
    if (keepParked) {
      oldPending.canvas = null;
      oldPending.context = null;
      oldPending.pixels = null;
    } else release(oldPending);
    // The completed-frame owner hands us immutable pixels; retaining that array
    // avoids an unbounded main-thread copy at the frame completion boundary.
    const next = recordFor(input, grid, 0, 0, grid.width, grid.height, false);
    pendingSource = next;
    sampleBytes();
    return true;
  }

  function queueTile(input) {
    if (disposed) return false;
    const grid = copyGrid(input.snapshot);
    const { x, y, width, height } = input;
    if (!grid || ![x, y, width, height].every(Number.isInteger) || x < 0 || y < 0 ||
        width <= 0 || height <= 0 || x + width > grid.width || y + height > grid.height) return false;
    const description = { grid, x, y, width, height, colorKey: input.colorKey ?? grid.colorKey };
    if (target && (!intersects(description, target) ||
        (targetColorKey !== undefined && targetColorKey !== description.colorKey))) {
      rejectedTiles++;
      return false;
    }
    if (allRecords().some(record => record.uploadedRows === record.height &&
        containsFiner(record, description))) return false;
    const bytes = width * height * 8 + RECORD_BYTES;
    if (!admit(bytes + width * 4, true)) { rejectedTiles++; return false; }
    const record = recordFor(input, grid, x, y, width, height);
    if (!record) return false;
    tiles.push(record);
    sampleBytes();
    return true;
  }

  function makeBacking(record) {
    if (record.canvas) return true;
    try {
      record.canvas = createCanvas();
      record.canvas.width = record.width;
      record.canvas.height = record.height;
      record.context = record.canvas.getContext('2d');
      return !!record.context?.putImageData && !!record.context.createImageData;
    } catch (error) { return false; }
  }

  function upload(record, remainingPixels) {
    if (!makeBacking(record)) return 0;
    const available = maxBytes - residentBytes();
    const rows = Math.min(record.height - record.uploadedRows,
      Math.max(1, Math.floor(maxStripPixels / record.width)),
      Math.floor(remainingPixels / record.width), Math.floor(available / (record.width * 4)));
    if (rows <= 0) return 0;
    const count = record.width * rows;
    stagingBytes = count * 4;
    sampleBytes();
    try {
      const image = record.context.createImageData(record.width, rows);
      const start = record.uploadedRows * record.width * 4;
      image.data.set(record.pixels.subarray(start, start + count * 4));
      record.context.putImageData(image, 0, record.uploadedRows);
      record.uploadedRows += rows;
      if (record !== source && record !== pendingSource && record.uploadedRows === record.height) record.pixels = null;
      uploadedPixels += count;
      return count;
    } finally { stagingBytes = 0; }
  }

  function removeDominated() {
    const completedRecords = allRecords().filter(record => record.uploadedRows === record.height);
    for (let index = tiles.length - 1; index >= 0; index--) {
      const tile = tiles[index];
      if (completedRecords.some(record => record !== tile && containsFiner(record, tile) &&
          // Two identical regions keep the newest original, avoiding mutual eviction.
          (!containsFiner(tile, record) || record.id > tile.id))) evict(index);
    }
  }

  function snapshotStats() {
    const records = allRecords();
    const accepted = records.filter(record => record.uploadedRows > 0);
    const pending = records.filter(record => record.uploadedRows < record.height);
    const area = canvas.width * canvas.height || 1;
    return { supported: true, disposed, pendingTiles: tiles.filter(tile => tile.uploadedRows < tile.height).length,
      pendingUploads: pending.length, acceptedTiles: tiles.filter(tile => tile.uploadedRows > 0).length,
      acceptedCoverage: Math.min(1, coveredArea(lastRegions.map(region => region.clipped).filter(Boolean)) / area),
      currentCoverage: Math.min(1, coveredArea(lastRegions.filter(region => region.current)
        .map(region => region.clipped).filter(Boolean)) / area),
      residentBytes: residentBytes(), peakBytes, maxBytes, uploadedPixels, lastUploadPixels,
      lastInstallMs, budgetExhausted, evictedTiles, rejectedTiles,
      sourceCameraRequestedAt: source?.cameraRequestedAt ?? null,
      sourceRanges: source ? Object.freeze({ xRange: source.grid.xRange, yRange: source.grid.yRange }) : null,
      sourceWidth: source?.grid.width ?? null, sourceHeight: source?.grid.height ?? null,
      sourceColorKey: source?.colorKey, sourceReady: !!source, pendingSource: !!pendingSource,
      exactCurrent: !!source && !!target && sameGrid(source.grid, target, canvas) &&
        (targetColorKey === undefined || targetColorKey === source.colorKey) &&
        lastRegions.every(region => region.current),
      sourceCameraAgeMs: source && target && lastPresentTime !== null
        ? (sameGrid(source.grid, target, canvas) ? 0 : Math.max(0, lastPresentTime - source.cameraRequestedAt)) : null,
      latestCameraRequestedAt,
      oldestAcceptedCameraRequestedAt: accepted.length
        ? Math.min(...accepted.map(record => record.cameraRequestedAt)) : null,
      ...anchorStats,
      regions: lastRegions.map(region => { const result = { ...region }; delete result.clipped; return result; }) };
  }

  function measureAnchor(regions, anchor) {
    // A bounded sample lattice keeps diagnostics out of the display-sized hot
    // path. These sampled values supplement the full rectangular coverage union.
    const radius = anchor?.radius ?? 32 * canvas.width / (canvas.clientWidth || canvas.width);
    const centerX = anchor?.x ?? canvas.width / 2, centerY = anchor?.y ?? canvas.height / 2;
    const finestFirst = regions.length ? [...regions].reverse() : null;
    let samples = 0, covered = 0, current = 0, oldest = 0, footprint = 0;
    for (let row = 0; row < 16; row++) {
      for (let column = 0; column < 16; column++) {
        const dx = ((column + 0.5) / 16 * 2 - 1) * radius;
        const dy = ((row + 0.5) / 16 * 2 - 1) * radius;
        const x = centerX + dx, y = centerY + dy;
        if (dx * dx + dy * dy > radius * radius || x < 0 || y < 0 || x >= canvas.width || y >= canvas.height) continue;
        samples++;
        if (!finestFirst) continue;
        const region = finestFirst.find(rect => x >= rect.x && y >= rect.y &&
          x < rect.x + rect.width && y < rect.y + rect.height);
        if (!region) continue;
        covered++;
        if (region.current) current++;
        oldest = Math.max(oldest, region.sourceCameraAgeMs);
        footprint = Math.max(footprint, region.sampleX, region.sampleY);
      }
    }
    return { anchorCoverage: samples ? covered / samples : 0,
      anchorCurrentCoverage: samples ? current / samples : 0,
      anchorMaxSourceCameraAgeMs: covered ? oldest : null,
      anchorMaxSampleFootprint: covered ? footprint : null,
      anchorMissingSamples: samples - covered, anchorSampleCount: samples };
  }

  function present(ranges, { budgetMs = 2, now = clock, colorKey,
    cameraRequestedAt, anchor, sourceOnly = false, preserveCanvasUntilSourceReady = false } = {}) {
    if (disposed || !copyGrid({ ...ranges, width: canvas.width, height: canvas.height })) return null;
    target = { xRange: [...ranges.xRange], yRange: [...ranges.yRange] };
    targetColorKey = colorKey;
    if (Number.isFinite(cameraRequestedAt)) latestCameraRequestedAt = cameraRequestedAt;
    const readTime = typeof now === 'function' ? now : () => now;
    const start = readTime();
    lastUploadPixels = 0;
    if (!sourceOnly) evictOutsideView();
    // Small tiles reveal useful detail before a large completed source copy.
    const pending = (sourceOnly ? [] : [...tiles, pendingSource]).filter(record => record &&
      record.uploadedRows < record.height && (colorKey === undefined || record.colorKey === colorKey));
    for (const record of pending) {
      while (record.uploadedRows < record.height && lastUploadPixels < maxUploadPixels &&
          readTime() - start < Math.max(0, budgetMs)) {
        const count = upload(record, maxUploadPixels - lastUploadPixels);
        if (!count) break;
        lastUploadPixels += count;
      }
      if (lastUploadPixels >= maxUploadPixels || readTime() - start >= Math.max(0, budgetMs)) break;
    }
    if (pendingSource && pendingSource.uploadedRows === pendingSource.height) {
      release(source);
      source = pendingSource;
      pendingSource = null;
      if (sameGrid(source.grid, target, canvas)) {
        // A final exact source owns the requested grid. Optional mixed-camera
        // overlays must not alter its completed oracle or history identity.
        for (let index = tiles.length - 1; index >= 0; index--) evict(index);
      }
    }
    if (!sourceOnly) removeDominated();
    if (!sourceOnly && source && sameGrid(source.grid, target, canvas) &&
        (colorKey === undefined || source.colorKey === colorKey)) {
      // Reversing input can return to an already accepted exact frame. Let that
      // original own the uniform requested grid without requiring recomputation.
      for (let index = tiles.length - 1; index >= 0; index--) evict(index);
    }
    lastInstallMs = Math.max(0, readTime() - start);
    budgetExhausted = allRecords().some(record => record.uploadedRows < record.height) &&
      (lastUploadPixels >= maxUploadPixels || lastInstallMs >= Math.max(0, budgetMs));
    const currentTime = readTime();
    lastPresentTime = currentTime;
    if (preserveCanvasUntilSourceReady && !source) {
      // A helper seeded from an existing uniform visible frame can prepare its
      // original backing without clearing not-yet-installed rows of that frame.
      lastRegions = [];
      anchorStats = measureAnchor([], anchor);
      const stats = snapshotStats();
      canvas._fractalCpuPresentation = stats;
      return stats;
    }
    const drawable = (sourceOnly ? [source].filter(Boolean) : allRecords()).filter(record => record.uploadedRows > 0 &&
      (colorKey === undefined || record.colorKey === colorKey))
      .map(record => ({ record, rect: projection(record, target, canvas) }))
      .filter(({ rect }) => clipped(rect, canvas.width, canvas.height))
      .sort((a, b) => Math.max(b.rect.sampleX, b.rect.sampleY) - Math.max(a.rect.sampleX, a.rect.sampleY) ||
        a.record.id - b.record.id);
    context.save?.();
    try {
      context.globalAlpha = 1;
      context.globalCompositeOperation = 'source-over';
      context.imageSmoothingEnabled = true;
      context.fillStyle = BACKGROUND;
      context.fillRect(0, 0, canvas.width, canvas.height);
      for (const { record, rect } of drawable) {
        // Transparent fractal pixels reveal the page background, never an
        // unrelated coarser sample from underneath this accepted region.
        context.fillRect(rect.x, rect.y, rect.width, rect.height);
        context.drawImage(record.canvas, 0, 0, record.width, record.uploadedRows,
          rect.x, rect.y, rect.width, rect.height);
      }
    } finally { context.restore?.(); }
    lastRegions = drawable.map(({ record, rect }) => ({ id: record.id, ...rect,
      clipped: clipped(rect, canvas.width, canvas.height),
      cameraRequestedAt: record.cameraRequestedAt,
      sourceCameraAgeMs: rect.current ? 0 : Math.max(0, currentTime - record.cameraRequestedAt),
      xRange: [...record.grid.xRange], yRange: [...record.grid.yRange],
      gridWidth: record.grid.width, gridHeight: record.grid.height }));
    anchorStats = measureAnchor(lastRegions, anchor);
    const stats = snapshotStats();
    canvas._fractalCpuPresentation = stats;
    return stats;
  }

  function reset() {
    allRecords().forEach(release);
    source = null;
    pendingSource = null;
    tiles = [];
    target = null;
    targetColorKey = undefined;
    latestCameraRequestedAt = null;
    lastRegions = [];
    lastPresentTime = null;
    anchorStats = { anchorCoverage: 0, anchorCurrentCoverage: 0,
      anchorMaxSourceCameraAgeMs: null, anchorMaxSampleFootprint: null, anchorMissingSamples: 0 };
    canvas._fractalCpuPresentation = null;
  }

  return { setSource, queueTile, present, reset,
    dispose() { reset(); disposed = true; },
    get stats() { return snapshotStats(); } };
}
